import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Upload, Check, Image, Link, Star } from 'lucide-react';
interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (video: any) => void;
  categories: string[];
}
const THUMBNAIL_OPTIONS = [{
  id: '1',
  url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
  label: 'Meeting'
}, {
  id: '2',
  url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
  label: 'Dashboard'
}, {
  id: '3',
  url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
  label: 'Documents'
}, {
  id: '4',
  url: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80',
  label: 'Technology'
}, {
  id: '5',
  url: 'https://images.unsplash.com/photo-1593642632823-8f78536788c6?w=800&q=80',
  label: 'Remote Work'
}, {
  id: '6',
  url: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&q=80',
  label: 'Communication'
}, {
  id: '7',
  url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
  label: 'Finance'
}, {
  id: '8',
  url: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80',
  label: 'Team'
}];
export function VideoUploadModal({
  isOpen,
  onClose,
  onUpload,
  categories
}: VideoUploadModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(categories.filter(c => c !== 'All')[0] || 'Onboarding');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(THUMBNAIL_OPTIONS[0].url);
  const [customThumbnailUrl, setCustomThumbnailUrl] = useState('');
  const [thumbnailMode, setThumbnailMode] = useState<'preset' | 'custom'>('preset');
  const [isRequired, setIsRequired] = useState(false);
  const availableCategories = categories.filter(c => c !== 'All');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const thumbnailUrl = thumbnailMode === 'custom' && customThumbnailUrl ? customThumbnailUrl : selectedThumbnail;
    onUpload({
      id: Math.random().toString(36).substr(2, 9),
      title,
      description,
      category,
      duration: '5:30',
      dateAdded: new Date().toISOString(),
      thumbnailUrl,
      isRequired
    });
    setTitle('');
    setDescription('');
    setCategory(availableCategories[0] || 'Onboarding');
    setSelectedThumbnail(THUMBNAIL_OPTIONS[0].url);
    setCustomThumbnailUrl('');
    setThumbnailMode('preset');
    setIsRequired(false);
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Upload New Video" maxWidth="2xl" data-id="element-814">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-815">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-816">
          <div data-id="element-817">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-818">
              Video Title
            </label>
            <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="e.g., New Employee Orientation 2024" data-id="element-819" />
          </div>
          <div data-id="element-820">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-821">
              Category
            </label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" data-id="element-822">
              {availableCategories.map(cat => <option key={cat} value={cat} data-id="element-823">
                  {cat}
                </option>)}
            </select>
          </div>
        </div>
        <div data-id="element-824">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-825">
            Description
          </label>
          <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="Brief description of the video content..." data-id="element-826" />
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-827">
          <div className="flex items-center h-5" data-id="element-828">
            <input id="isRequiredVideo" type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded" data-id="element-829" />
          </div>
          <div className="flex-1" data-id="element-830">
            <label htmlFor="isRequiredVideo" className="font-medium text-gray-700 flex items-center gap-2" data-id="element-831">
              Mark as Required
              <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" data-id="element-832" />
            </label>
            <p className="text-xs text-gray-500" data-id="element-833">
              Employees will be required to watch this video to complete their
              training.
            </p>
          </div>
        </div>
        <div data-id="element-834">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-835">
            Thumbnail
          </label>
          <div className="flex gap-2 mb-3" data-id="element-836">
            <button type="button" onClick={() => setThumbnailMode('preset')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${thumbnailMode === 'preset' ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-837">
              <Image className="w-4 h-4" data-id="element-838" />
              Choose from library
            </button>
            <button type="button" onClick={() => setThumbnailMode('custom')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${thumbnailMode === 'custom' ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-839">
              <Link className="w-4 h-4" data-id="element-840" />
              Custom URL
            </button>
          </div>
          {thumbnailMode === 'preset' ? <div className="grid grid-cols-4 gap-2" data-id="element-841">
              {THUMBNAIL_OPTIONS.map(thumb => <button key={thumb.id} type="button" onClick={() => setSelectedThumbnail(thumb.url)} className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${selectedThumbnail === thumb.url ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-842">
                  <img src={thumb.url} alt={thumb.label} className="w-full h-full object-cover" data-id="element-843" />
                  {selectedThumbnail === thumb.url && <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center" data-id="element-844">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center" data-id="element-845">
                        <Check className="w-4 h-4 text-white" data-id="element-846" />
                      </div>
                    </div>}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1" data-id="element-847">
                    <span className="text-[10px] text-white font-medium" data-id="element-848">
                      {thumb.label}
                    </span>
                  </div>
                </button>)}
            </div> : <div className="space-y-3" data-id="element-849">
              <input type="url" value={customThumbnailUrl} onChange={e => setCustomThumbnailUrl(e.target.value)} placeholder="https://example.com/thumbnail.jpg" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm" data-id="element-850" />
              {customThumbnailUrl && <div className="relative w-48 aspect-video rounded-lg overflow-hidden border border-gray-200" data-id="element-851">
                  <img src={customThumbnailUrl} alt="Custom thumbnail preview" className="w-full h-full object-cover" onError={e => {
              ;
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x225?text=Invalid+URL';
            }} data-id="element-852" />
                </div>}
            </div>}
        </div>
        <div data-id="element-853">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-854">
            Video File
          </label>
          <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400'}`} onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
        }} data-id="element-855">
            <div className="mx-auto h-10 w-10 text-gray-400" data-id="element-856">
              <Upload className="h-full w-full" data-id="element-857" />
            </div>
            <p className="mt-2 text-sm text-gray-600" data-id="element-858">
              Drag and drop your video file here, or click to browse
            </p>
            <p className="mt-1 text-xs text-gray-500" data-id="element-859">
              MP4, WebM or Ogg up to 500MB
            </p>
            <button type="button" className="mt-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500" data-id="element-860">
              Select File
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-861">
          <Button variant="secondary" onClick={onClose} type="button" data-id="element-862">
            Cancel
          </Button>
          <Button variant="primary" type="submit" data-id="element-863">
            Upload Video
          </Button>
        </div>
      </form>
    </Modal>;
}